import { useState } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';

/**
 * Modern SalesChart Component
 * Displays sales data with multiple chart types and time periods
 */
const SalesChart = ({ data = [], title = "Sales Overview" }) => {
    const [chartType, setChartType] = useState('area'); // 'line', 'area', 'bar'

    // Sample data if none provided
    const defaultData = [
        { name: 'Mon', sales: 4000, orders: 24 },
        { name: 'Tue', sales: 3000, orders: 18 },
        { name: 'Wed', sales: 5000, orders: 32 },
        { name: 'Thu', sales: 4500, orders: 28 },
        { name: 'Fri', sales: 6000, orders: 38 },
        { name: 'Sat', sales: 5500, orders: 35 },
        { name: 'Sun', sales: 4800, orders: 30 },
    ];

    const chartData = data.length > 0 ? data : defaultData;

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="sales-chart__tooltip">
                    <p className="sales-chart__tooltip-label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="sales-chart__tooltip-value" style={{ color: entry.color }}>
                            {entry.name}: ₹{entry.value.toLocaleString()}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Render chart based on type
    const renderChart = () => {
        const commonProps = {
            data: chartData,
            margin: { top: 10, right: 30, left: 0, bottom: 0 }
        };

        switch (chartType) {
            case 'line':
                return (
                    <LineChart {...commonProps}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
                        <XAxis dataKey="name" stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <YAxis stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '20px' }} />
                        <Line
                            type="monotone"
                            dataKey="sales"
                            stroke="#2196F3"
                            strokeWidth={3}
                            dot={{ fill: '#2196F3', r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                );

            case 'area':
                return (
                    <AreaChart {...commonProps}>
                        <defs>
                            <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2196F3" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#2196F3" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
                        <XAxis dataKey="name" stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <YAxis stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '20px' }} />
                        <Area
                            type="monotone"
                            dataKey="sales"
                            stroke="#2196F3"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorSales)"
                        />
                    </AreaChart>
                );

            case 'bar':
                return (
                    <BarChart {...commonProps}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
                        <XAxis dataKey="name" stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <YAxis stroke="#4A4A4A" style={{ fontSize: '13px' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '20px' }} />
                        <Bar dataKey="sales" fill="#2196F3" radius={[8, 8, 0, 0]} />
                    </BarChart>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}>
                    {/* Left Group: Title + Chart Type Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, marginTop: -12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <TrendingUp size={20} color="#0a0a0a" />
                            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#0a0a0a', margin: 0 }}>{title}</h2>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', background: '#f3f4f6', padding: '4px', borderRadius: '6px', width: 'fit-content' }}>
                            <button
                                onClick={() => setChartType('line')}
                                title="Line Chart"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '36px',
                                    height: '36px',
                                    border: 'none',
                                    background: chartType === 'line' ? '#0a0a0a' : 'transparent',
                                    color: chartType === 'line' ? '#ffffff' : '#6b7280',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                }}
                                onMouseEnter={(e) => {
                                    if (chartType !== 'line') {
                                        e.target.style.background = '#e5e7eb';
                                        e.target.style.color = '#0a0a0a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (chartType !== 'line') {
                                        e.target.style.background = 'transparent';
                                        e.target.style.color = '#6b7280';
                                    }
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                </svg>
                            </button>
                            <button
                                onClick={() => setChartType('area')}
                                title="Area Chart"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '36px',
                                    height: '36px',
                                    border: 'none',
                                    background: chartType === 'area' ? '#0a0a0a' : 'transparent',
                                    color: chartType === 'area' ? '#ffffff' : '#6b7280',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                }}
                                onMouseEnter={(e) => {
                                    if (chartType !== 'area') {
                                        e.target.style.background = '#e5e7eb';
                                        e.target.style.color = '#0a0a0a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (chartType !== 'area') {
                                        e.target.style.background = 'transparent';
                                        e.target.style.color = '#6b7280';
                                    }
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 3v18h18"></path>
                                    <path d="M18 17V9l-5 5-5-5v8"></path>
                                </svg>
                            </button>
                            <button
                                onClick={() => setChartType('bar')}
                                title="Bar Chart"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '36px',
                                    height: '36px',
                                    border: 'none',
                                    background: chartType === 'bar' ? '#0a0a0a' : 'transparent',
                                    color: chartType === 'bar' ? '#ffffff' : '#6b7280',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                }}
                                onMouseEnter={(e) => {
                                    if (chartType !== 'bar') {
                                        e.target.style.background = '#e5e7eb';
                                        e.target.style.color = '#0a0a0a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (chartType !== 'bar') {
                                        e.target.style.background = 'transparent';
                                        e.target.style.color = '#6b7280';
                                    }
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="20" x2="12" y2="10"></line>
                                    <line x1="18" y1="20" x2="18" y2="4"></line>
                                    <line x1="6" y1="20" x2="6" y2="16"></line>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Right Group: Total Sales */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0 }}>Total Sales</span>
                        <span style={{ fontSize: '24px', fontWeight: 700, color: '#0a0a0a' }}>
                            ₹{chartData.reduce((sum, item) => sum + item.sales, 0).toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ width: '100%', marginTop: 0, padding: '0 24px' }}>
                <ResponsiveContainer width="100%" height={350}>
                    {renderChart()}
                </ResponsiveContainer>
            </div>
        </>
    );
};

export default SalesChart;
